import React, { useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import {
  Typography, Box, Button, Paper, Grid,
  CircularProgress, Tooltip, Checkbox,
} from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { useAptorApi } from '../../../../../Api';
import { ComplianceTable } from '../CarryLK/List/ComplianceTable';
import { HtmlView, MTable, RepealableName } from '../../../../../Components';
import { OwnNotes } from '../../../../../Components/OwnNotes';
import { AccessLevel, LawPortalFeature, UserContext } from '../../../../../Context/UserContext/UserContext';
import { useIntl } from 'react-intl';
import { TextField as AppTextField, SelectField, DateField } from '../../../../../Components/Field';
import { TypedFormFieldValue } from '../../../../../Components/Form/Form.types';
import moment from 'moment';
import { PlanningView } from '../PlanningLK/View/PlanningView';

// App theme primary color
const PRIMARY = '#c4122f';

// ─────────────────────────────────────────────
// localStorage persistence helpers
// ─────────────────────────────────────────────
const buildKey = (userId: number | null) => `createPlanWizard_session_user_${userId ?? 'anon'}`;

export const saveWizardSession = (userId: number | null, data: object) => {
  try {
    localStorage.setItem(buildKey(userId), JSON.stringify(data));
  } catch (e) {
    console.warn('saveWizardSession failed', e);
  }
};

export const clearWizardSession = (userId: number | null) => {
  try {
    localStorage.removeItem(buildKey(userId));
  } catch (e) {
    console.warn('clearWizardSession failed', e);
  }
};

export const loadWizardSession = (userId: number | null): WizardSession | null => {
  try {
    const raw = localStorage.getItem(buildKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
export interface WizardSession {
  planType: 'one-time' | 'recurring' | 'from-completed' | 'create';
  currentStep: number;
  stepData: StepData;
  selectedRows: any[];
  selectedRowsData: any[];
}

interface IProps {
  open: boolean;
  // 'create' is the new value — wizard shows a plan-type picker as step 1
  planType: 'one-time' | 'recurring' | 'from-completed' | 'create';
  onClose: () => void;
  getCompliancePlanList: () => void;
  editPlan?: any;
  resumeSession?: WizardSession | null;
}

interface StepData {
  RecurrentInterval?: number;
  CustomerCompanyUnitId?: number;
  _companyUnitName?: string;
  StartDate?: string;
  EndDate?: string;
  _sendReminder?: boolean;
  Reminder?: number;
  _reminderLabel?: string;
  type?: number;
  _typeName?: string;
  name?: string;
  _editPlanId?: number;
  _selectedPlanId?: string | null;
  // Stores the resolved plan type after the user picks in the first step
  _resolvedPlanType?: 'one-time' | 'recurring';
}

// ─────────────────────────────────────────────
// Shared button bar
// ─────────────────────────────────────────────
const outlinedRedBtn: React.CSSProperties = { fontWeight: 700, borderColor: PRIMARY, color: PRIMARY };

const StepButtonBar = ({
  onCancel,
  onBack,
  onNext,
  nextLabel,
  nextDisabled = false,
  isLoading = false,
  showBack = true,
  formatMessage,
}: {
  onCancel: () => void;
  onBack?: () => void;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
  isLoading?: boolean;
  showBack?: boolean;
  formatMessage: any;
}) => (
  <Box style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Box>
      <Button variant="outlined" onClick={onCancel} style={outlinedRedBtn}>
        {formatMessage({ id: 'form.cancel' })}
      </Button>
    </Box>
    <Box style={{ display: 'flex' }}>
      {showBack && onBack && (
        <Button variant="outlined" onClick={onBack} style={{ ...outlinedRedBtn, marginRight: '8px' }}>
          {formatMessage({ id: 'component.back-button.back' })}
        </Button>
      )}
      <Button
        variant="outlined"
        onClick={onNext}
        disabled={nextDisabled || isLoading}
        style={{ ...outlinedRedBtn, minWidth: '140px' }}
      >
        {isLoading ? <CircularProgress size={20} /> : formatMessage({ id: nextLabel })}
      </Button>
    </Box>
  </Box>
);

// ─────────────────────────────────────────────
// Main Wizard
// ─────────────────────────────────────────────
export const CreatePlanWizard = ({
  open,
  planType: propPlanType,
  onClose,
  getCompliancePlanList,
  editPlan,
  resumeSession,
}: IProps) => {
  const { companyUnits, customerId, id: userId, hasAccessToFeature } = useContext(UserContext);
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCompanyUnit, setSelectedCompanyUnit] = useState<any>(null);
  const [selectedRequirementType, setSelectedRequirementType] = useState<any>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stepData, setStepData] = useState<StepData>({});
  const [editInitialized, setEditInitialized] = useState(!editPlan);

  // When propPlanType is 'create', the actual plan type is chosen in step 1
  // and stored in stepData._resolvedPlanType. This derived value drives step logic.
  const resolvedPlanType: 'one-time' | 'recurring' | 'from-completed' =
    propPlanType === 'create'
      ? (stepData._resolvedPlanType ?? 'one-time') // fallback; step list accounts for this
      : propPlanType;

  const sessionHydrated = useRef(false);

  const REMINDER_OPTIONS = useMemo(() => [
    { id: 0, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.week' }, { number: 1 }) },
    { id: 1, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.weeks' }, { number: 2 }) },
    { id: 2, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.weeks' }, { number: 3 }) },
    { id: 3, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.month' }, { number: 1 }) },
    { id: 4, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.months' }, { number: 2 }) },
    { id: 5, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.months' }, { number: 3 }) },
    { id: 6, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.months' }, { number: 6 }) },
    { id: 7, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.year' }, { number: 1 }) },
  ], [formatMessage]);

  const accessToOurLaws = hasAccessToFeature(LawPortalFeature.OurLaws, AccessLevel.Read);
  const accessToOtherRequirements = hasAccessToFeature(LawPortalFeature.OtherRequirements, AccessLevel.Read);

  const requirementTypeOptions = useMemo(() => {
    const opts: { id: number; name: string }[] = [];
    if (accessToOurLaws) opts.push({ id: 0, name: formatMessage({ id: 'law-portal.plan.type.law', defaultMessage: 'Legislation' }) });
    if (accessToOtherRequirements) opts.push({ id: 1, name: formatMessage({ id: 'law-portal.plan.type.other-req', defaultMessage: 'Other requirements' }) });
    return opts;
  }, [accessToOurLaws, accessToOtherRequirements, formatMessage]);

  // ── Hydration: resume session ─────────────────────────────────────────────
  useEffect(() => {
    if (editPlan || sessionHydrated.current || !resumeSession) return;
    sessionHydrated.current = true;

    setCurrentStep(resumeSession.currentStep);
    setStepData(resumeSession.stepData);
    setSelectedRows(resumeSession.selectedRows ?? []);
    setSelectedRowsData(resumeSession.selectedRowsData ?? []);

    const compUnit = companyUnits.find(u => u.id === resumeSession.stepData.CustomerCompanyUnitId);
    const reqType = requirementTypeOptions.find(t => t.id === resumeSession.stepData.type);
    if (compUnit) setSelectedCompanyUnit(compUnit);
    if (reqType) setSelectedRequirementType(reqType);

    setEditInitialized(true);
  }, [resumeSession, editPlan, companyUnits, requirementTypeOptions]);

  // ── Hydration: edit mode ──────────────────────────────────────────────────
  useEffect(() => {
    if (!editPlan) {
      if (!resumeSession) {
        if (companyUnits.length === 1) {
          setSelectedCompanyUnit(companyUnits[0]);
          setStepData(prev => ({ ...prev, CustomerCompanyUnitId: companyUnits[0].id, _companyUnitName: companyUnits[0].name }));
        }
        if (requirementTypeOptions.length === 1) {
          setSelectedRequirementType(requirementTypeOptions[0]);
          setStepData(prev => ({ ...prev, type: requirementTypeOptions[0].id, _typeName: requirementTypeOptions[0].name }));
        }
      }
      return;
    }

    const compUnit = companyUnits.find(u => u.id === (editPlan.customerCompanyUnitId?.id ?? editPlan.customerCompanyUnitId));
    const reqType = requirementTypeOptions.find(t => t.id === editPlan.type);
    const reminderOpt = editPlan.reminder != null ? REMINDER_OPTIONS.find((o: any) => o.id === editPlan.reminder) : null;

    if (compUnit) setSelectedCompanyUnit(compUnit);
    if (reqType) setSelectedRequirementType(reqType);

    setStepData({
      _editPlanId: editPlan.id ?? editPlan._planId,
      name: editPlan.name,
      CustomerCompanyUnitId: compUnit?.id,
      _companyUnitName: compUnit?.name || editPlan.customerCompanyUnitId?.name,
      StartDate: editPlan.startDate ? moment(editPlan.startDate).format('YYYY-MM-DD') : undefined,
      EndDate: editPlan.endDate ? moment(editPlan.endDate).format('YYYY-MM-DD') : undefined,
      type: editPlan.type,
      _typeName: reqType?.name,
      RecurrentInterval: editPlan.recurrentInterval || undefined,
      Reminder: editPlan.reminder,
      _reminderLabel: reminderOpt?.name,
      _sendReminder: editPlan.reminder != null,
    });

    const fetchLaws = async () => {
      try {
        const result = await api.getPlanAllLawID(editPlan.id ?? editPlan._planId);
        setSelectedRows(result?.legislationIds || []);
      } catch (e) {
        console.error('Error fetching plan laws for edit:', e);
      } finally {
        setEditInitialized(true);
      }
    };
    fetchLaws();
  }, [editPlan, companyUnits, requirementTypeOptions]);

  // ── Persist to localStorage ───────────────────────────────────────────────
  useEffect(() => {
    if (!open || editPlan) return;
    // For 'create' flow: don't save until the user has completed step 1
    // (i.e. _resolvedPlanType is set). Before that there's nothing meaningful to resume.
    if (propPlanType === 'create' && !stepData._resolvedPlanType) return;
    if (currentStep === 1 && Object.keys(stepData).length === 0) return;
    const session: WizardSession = {
      planType: propPlanType,
      currentStep,
      stepData,
      selectedRows,
      selectedRowsData,
    };
    saveWizardSession(userId, session);
  }, [open, editPlan, propPlanType, currentStep, stepData, selectedRows, selectedRowsData, userId]);

  // ── Step list ─────────────────────────────────────────────────────────────
  const getActiveSteps = useCallback(() => {
    const steps: string[] = [];

    // When propPlanType is 'create', the very first step is the type picker.
    // After the user picks, _resolvedPlanType is set and the rest of the list
    // is built dynamically based on that value.
    if (propPlanType === 'create') {
      steps.push('select-plan-type'); // NEW step
    }

    if (propPlanType === 'from-completed') steps.push('select-plan');

    // 'recurring' or resolved-to-recurring need the days step
    const effectiveType = propPlanType === 'create' ? resolvedPlanType : propPlanType;
    if (effectiveType === 'recurring' || propPlanType === 'from-completed') steps.push('days');

    steps.push('company-unit');
    // steps.push('dates');
    // steps.push('reminder');
    // steps.push('requirement-type');
    // steps.push('laws-table');
    // steps.push('plan-name');
    // steps.push('preview');
    return steps;
  }, [propPlanType, resolvedPlanType]);

  const activeSteps = useMemo(() => getActiveSteps(), [getActiveSteps]);
  const currentStepName = activeSteps[currentStep - 1];
  const totalSteps = activeSteps.length;
  const isFirstStep = currentStep === 1;

  const handleStepSubmit = (data: Partial<StepData>) => {
    if (
      (data.CustomerCompanyUnitId !== undefined && data.CustomerCompanyUnitId !== stepData.CustomerCompanyUnitId) ||
      (data.type !== undefined && data.type !== stepData.type)
    ) {
      setSelectedRows([]);
      setSelectedRowsData([]);
    }
    setStepData(prev => ({ ...prev, ...data }));
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleCancel = () => {
    clearWizardSession(userId);
    setCurrentStep(1);
    setSelectedCompanyUnit(null);
    setSelectedRequirementType(null);
    setSelectedRows([]);
    setSelectedRowsData([]);
    setStepData({});
    onClose();
  };

  const handleFinalSubmit = async (previewData?: StepData) => {
    const finalData = previewData || stepData;
    const effectiveIsDirect =
      propPlanType === 'create'
        ? finalData._resolvedPlanType === 'one-time'
        : propPlanType === 'one-time';

    try {
      setIsLoading(true);
      const apiData: any = {
        name: finalData.name,
        CustomerCompanyUnitId: finalData.CustomerCompanyUnitId,
        StartDate: finalData.StartDate,
        EndDate: finalData.EndDate,
        type: finalData.type,
        isDirect: effectiveIsDirect,
      };
      if (finalData.Reminder != null) apiData.Reminder = finalData.Reminder;
      if (finalData.RecurrentInterval) apiData.RecurrentInterval = finalData.RecurrentInterval;

      if (finalData._editPlanId) {
        await api.updatePlanLaws(finalData._editPlanId, apiData);
        await api.addLaws(finalData._editPlanId, { Legislations: selectedRows, type: finalData.type });
      } else {
        const response: any = await api.createCompliancePlan(customerId, apiData);
        if (response?.id) {
          await api.addLaws(response.id, { Legislations: selectedRows, type: finalData.type });
        }
      }
      clearWizardSession(userId);
      onClose();
      getCompliancePlanList();
    } catch (error) {
      console.error('Error saving plan:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;
  if (!editInitialized) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderStepContent = () => {
    const common = { onBack: handleBack, onCancel: handleCancel, currentData: stepData, isFirstStep };

    switch (currentStepName) {

      // ── NEW: plan-type picker step ────────────────────────────────────────
      case 'select-plan-type':
        return (
          <StepSelectPlanType
            {...common}
            onNext={(resolved) => {
              // Store resolved type and advance; step list will rebuild with correct steps
              setStepData(prev => ({ ...prev, _resolvedPlanType: resolved }));
              setCurrentStep(prev => Math.min(prev + 1, totalSteps));
            }}
          />
        );

      case 'select-plan':
        return (
          <Step0SelectPlan
            {...common}
            customerId={customerId}
            api={api}
            requirementTypeOptions={requirementTypeOptions}
            companyUnits={companyUnits}
            onPlanSelected={(data, compUnit, reqType, rows, planId) => {
              const isNewPlan = planId !== stepData._selectedPlanId;
              if (isNewPlan) {
                setStepData(prev => ({
                  ...prev,
                  ...data,
                  _selectedPlanId: planId,
                  name: undefined,
                }));
                if (compUnit) setSelectedCompanyUnit(compUnit);
                if (reqType) setSelectedRequirementType(reqType);
                setSelectedRows(rows);
                setSelectedRowsData([]);
              } else {
                setStepData(prev => ({ ...prev, _selectedPlanId: planId }));
              }
              setCurrentStep(prev => Math.min(prev + 1, totalSteps));
            }}
          />
        );

      case 'days':
        return <Step1Days {...common} onNext={handleStepSubmit} />;

      case 'company-unit':
        return (
          <Step2CompanyUnit
            {...common}
            companyUnits={companyUnits}
            onNext={handleStepSubmit}
            onCompanyUnitSelect={setSelectedCompanyUnit}
          />
        );

      case 'dates':
        return <Step3Dates {...common} onNext={handleStepSubmit} />;

      case 'reminder':
        return <Step4Reminder {...common} onNext={handleStepSubmit} reminderOptions={REMINDER_OPTIONS} />;

      case 'requirement-type':
        return (
          <Step5RequirementType
            {...common}
            typeOptions={requirementTypeOptions}
            onNext={handleStepSubmit}
            onTypeSelect={setSelectedRequirementType}
          />
        );

      case 'laws-table':
        return (
          <Step6LawsTable
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={setSelectedRowsData}
            selectedRequirementType={selectedRequirementType}
            selectedCompanyUnit={selectedCompanyUnit}
            onNext={() => setCurrentStep(prev => prev + 1)}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );

      case 'plan-name':
        return <Step7PlanName {...common} onNext={handleStepSubmit} />;

      case 'preview':
        return (
          <Step8Preview
            planData={stepData}
            setStepData={setStepData}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={setSelectedRowsData}
            requirementType={selectedRequirementType}
            setSelectedCompanyUnit={setSelectedCompanyUnit}
            setSelectedRequirementType={setSelectedRequirementType}
            companyUnits={companyUnits}
            requirementTypeOptions={requirementTypeOptions}
            reminderOptions={REMINDER_OPTIONS}
            onSubmit={handleFinalSubmit}
            onBack={handleBack}
            onCancel={handleCancel}
            isLoading={isLoading}
            // Pass the effective plan type so the preview knows whether to show interval field
            planType={
              propPlanType === 'create'
                ? (stepData._resolvedPlanType ?? 'one-time')
                : propPlanType
            }
          />
        );

      default:
        return null;
    }
  };

  return <Box style={{ minHeight: '420px' }}>{renderStepContent()}</Box>;
};

// ─────────────────────────────────────────────
// Step interfaces
// ─────────────────────────────────────────────
interface StepProps {
  onNext: (data: Partial<StepData>) => void;
  onBack: () => void;
  onCancel: () => void;
  currentData: StepData;
  isFirstStep: boolean;
}

// ─────────────────────────────────────────────
// NEW Step: Select Plan Type (One Time / Recurring)
// ─────────────────────────────────────────────
const StepSelectPlanType = ({
  onBack,
  onCancel,
  isFirstStep,
  currentData,
  onNext,
}: Omit<StepProps, 'onNext'> & {
  onNext: (resolved: 'one-time' | 'recurring') => void;
}) => {
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState<'one-time' | 'recurring' | null>(
    currentData._resolvedPlanType ?? null,
  );

  const options: { id: 'one-time' | 'recurring'; labelId: string; defaultLabel: string }[] = [
    {
      id: 'one-time',
      labelId: 'law-portal.legal.compliance.sub-menu.planning.one.time',
      defaultLabel: 'One Time',
    },
    {
      id: 'recurring',
      labelId: 'law-portal.legal.compliance.sub-menu.planning.recurring.plan',
      defaultLabel: 'Recurring',
    },
  ];

  return (
    <Paper variant="outlined" style={{ padding: '32px 48px', minHeight: '200px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '32px', lineHeight: 1.4, textAlign: 'center' }}>
          {formatMessage({
            id: 'law-portal.evalution.plan.select.type.title',
            defaultMessage: 'Select Plan Type',
          })}
        </Typography>
        <Box style={{ maxWidth: 460, width: '100%' }}>
          <Grid container spacing={2} justifyContent="center">
            {options.map(opt => (
              <Grid item key={opt.id} xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setSelected(opt.id)}
                  style={{
                    padding: '14px 20px',
                    fontSize: '14px',
                    fontWeight: 700,
                    borderColor: PRIMARY,
                    color: selected === opt.id ? '#fff' : PRIMARY,
                    background: selected === opt.id ? PRIMARY : 'transparent',
                  }}
                >
                  {formatMessage({ id: opt.labelId, defaultMessage: opt.defaultLabel })}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      </div>
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={() => { if (selected) onNext(selected); }}
        nextLabel="form.save.and.back"
        nextDisabled={!selected}
        showBack={!isFirstStep}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Step 0: Select completed plan
// ─────────────────────────────────────────────
const Step0SelectPlan = ({
  onBack, onCancel, isFirstStep, currentData,
  customerId, api, requirementTypeOptions, companyUnits,
  onPlanSelected,
}: Omit<StepProps, 'onNext'> & {
  customerId: number | null;
  api: any;
  requirementTypeOptions: { id: number; name: string }[];
  companyUnits: { id: number; name: string }[];
  onPlanSelected: (data: Partial<StepData>, compUnit: any, reqType: any, lawIds: number[], planId: string) => void;
}) => {
  const { formatMessage } = useIntl();
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedPlanIdRef = useRef<string | null>(currentData._selectedPlanId ?? null);
  const [selectionVersion, setSelectionVersion] = useState(0);

  const setSelectedPlanId = useCallback((id: string | null) => {
    selectedPlanIdRef.current = id;
    setSelectionVersion(v => v + 1);
  }, []);

  const togglePlan = useCallback((planId: string) => {
    const next = selectedPlanIdRef.current === planId ? null : planId;
    setSelectedPlanId(next);
  }, [setSelectedPlanId]);

  useEffect(() => {
    let cancelled = false;
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await api.getAllcompliancePlan(customerId, { filters: {}, search: '' });
        if (!cancelled) {
          const allPlans = response?.customerPlans || [];
          setPlans(
            allPlans
              .filter((p: any) => !p.isDirect && p.isActive && p.isArchived !== true)
              .map((p: any) => ({ ...p, _planId: String(p.id) })),
          );
        }
      } catch (e) {
        console.error('Error fetching plans:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPlans();
    return () => { cancelled = true; };
  }, [api, customerId]);

  const columns = useMemo(() => [
    {
      title: '',
      field: '__select',
      width: '48px',
      sorting: false,
      removable: false,
      hiddenByColumnsButton: true,
      render: (row: any) => (
        <Checkbox
          checked={selectedPlanIdRef.current === String(row._planId)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => togglePlan(String(row._planId))}
          style={{ color: PRIMARY }}
        />
      ),
    },
    { title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.name-of-control' }), field: 'name' },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Company-unit' }),
      field: 'customerCompanyUnitId',
      render: (row: any) => row?.customerCompanyUnitId?.name,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Start-date' }),
      field: 'startDate',
      render: (row: any) => moment(row?.startDate).format('YYYY-MM-DD'),
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.End-date' }),
      field: 'endDate',
      render: (row: any) => moment(row?.endDate).format('YYYY-MM-DD'),
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Recuring-interval' }),
      field: 'recurrentInterval',
      render: (row: any) => row?.recurrentInterval || '-',
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Number-of-controls' }),
      field: 'lawCount',
      render: (row: any) => row.lawCount,
      sorting: false,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [formatMessage, togglePlan, selectionVersion]);

  const handleNext = async () => {
    const currentId = selectedPlanIdRef.current;
    const plan = plans.find((p: any) => p._planId === currentId);
    if (!plan || isSubmitting) return;

    try {
      setIsSubmitting(true);
      let lawIds: number[] = [];
      try {
        const result = await api.getPlanAllLawID(plan._planId);
        lawIds = result?.legislationIds || [];
      } catch (e) {
        console.error('Error fetching plan laws:', e);
      }

      const compUnit = companyUnits.find(
        (u: any) => u.id === (plan.customerCompanyUnitId?.id ?? plan.customerCompanyUnitId),
      );
      const reqType = requirementTypeOptions.find((t: any) => t.id === plan.type);

      const data: Partial<StepData> = {
        CustomerCompanyUnitId: compUnit?.id,
        _companyUnitName: compUnit?.name || plan.customerCompanyUnitId?.name,
        StartDate: plan.startDate ? moment(plan.startDate).format('YYYY-MM-DD') : undefined,
        EndDate: plan.endDate ? moment(plan.endDate).format('YYYY-MM-DD') : undefined,
        type: plan.type,
        _typeName: reqType?.name,
        RecurrentInterval: plan.recurrentInterval || undefined,
        Reminder: plan.reminder ?? undefined,
        _sendReminder: plan.reminder != null,
      };

      onPlanSelected(data, compUnit || null, reqType || null, lawIds, plan._planId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentSelectedId = selectedPlanIdRef.current;

  return (
    <Paper variant="outlined" style={{ padding: '24px', minHeight: '200px' }}>
      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <MTable
          key={`plan-table-${selectionVersion}`}
          columns={columns}
          data={plans}
          options={{ detailPanelColumnAlignment: 'right', pageSizeOptions: [10, 25, 50, 100] }}
          onRowClick={(_: any, row: any) => {
            if (row) togglePlan(String(row._planId));
          }}
          detailPanel={[
            {
              tooltip: formatMessage({ id: 'text.expend' }),
              render: (rowData: any) => <PlanningView planLawsData={rowData?.rowData} />,
            },
          ]}
        />
      )}
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleNext}
        nextLabel="form.save.and.back"
        nextDisabled={!currentSelectedId || isSubmitting}
        isLoading={isSubmitting}
        showBack={!isFirstStep}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Step 1: Recurring days
// ─────────────────────────────────────────────
const Step1Days = ({ onNext, onBack, onCancel, currentData, isFirstStep }: StepProps) => {
  const [days, setDays] = useState<number | undefined>(currentData.RecurrentInterval);
  const { formatMessage } = useIntl();

  const handleNext = () => {
    if (days && days > 0) onNext({ RecurrentInterval: days });
  };

  return (
    <Paper variant="outlined" style={{ padding: '32px 48px', minHeight: '200px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '24px', lineHeight: 1.4, textAlign: 'center' }}>
          {formatMessage({ id: 'law-portal.evalution.recurring.interval.label' })}
        </Typography>
        <Box style={{ maxWidth: 360, width: '100%' }}>
          <AppTextField
            name="days"
            placeholder={formatMessage({ id: 'law-portal.legal.number.of.day' })}
            type="number"
            errorTexts={[]}
            initialState={currentData.RecurrentInterval != null ? { value: currentData.RecurrentInterval.toString() } : undefined}
            onChange={(val) => setDays(val?.value ? Number(val.value) : undefined)}
          />
        </Box>
      </div>
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleNext}
        nextLabel="form.save.and.back"
        nextDisabled={!days || days <= 0}
        showBack={!isFirstStep}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Step 2: Company unit
// ─────────────────────────────────────────────
const Step2CompanyUnit = ({
  onNext, onBack, onCancel, currentData, isFirstStep,
  companyUnits, onCompanyUnitSelect,
}: StepProps & { companyUnits: { id: number; name: string }[]; onCompanyUnitSelect: (u: any) => void }) => {
  const [selectedUnit, setSelectedUnit] = useState<{ id: number; name: string } | null>(
    currentData.CustomerCompanyUnitId != null
      ? companyUnits.find(u => u.id === currentData.CustomerCompanyUnitId) || null
      : null,
  );
  const { formatMessage } = useIntl();
  const selectOptions = useMemo(() => companyUnits.map(u => ({ value: u.id, label: u.name })), [companyUnits]);

  const handleChange = useCallback((val: TypedFormFieldValue<number> | undefined) => {
    if (val) {
      const unit = companyUnits.find(u => u.id === val.value);
      setSelectedUnit(unit || null);
    } else {
      setSelectedUnit(null);
    }
  }, [companyUnits]);

  const handleNext = () => {
    if (selectedUnit) {
      onCompanyUnitSelect(selectedUnit);
      onNext({ CustomerCompanyUnitId: selectedUnit.id, _companyUnitName: selectedUnit.name });
    }
  };

  return (
    <Paper variant="outlined" style={{ padding: '32px 48px', minHeight: '200px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '24px', lineHeight: 1.4, textAlign: 'center' }}>
          {formatMessage({ id: 'law-portal.evalution.conpany.unit.label' })}
        </Typography>
        <Box style={{ maxWidth: 360, width: '100%' }}>
          <SelectField
            name="company-unit"
            placeholder={formatMessage({ id: 'form.selectCompanyUnits' })}
            options={selectOptions}
            errorTexts={[]}
            initialState={selectedUnit ? { value: selectedUnit.id, label: selectedUnit.name } : undefined}
            onChange={handleChange}
          />
        </Box>
      </div>
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleNext}
        nextLabel="form.save.and.back"
        nextDisabled={!selectedUnit}
        showBack={!isFirstStep}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Step 3: Dates
// ─────────────────────────────────────────────
const Step3Dates = ({ onNext, onBack, onCancel, currentData, isFirstStep }: StepProps) => {
  const [startDate, setStartDate] = useState<string>(currentData.StartDate || '');
  const [endDate, setEndDate] = useState<string>(currentData.EndDate || '');
  const { formatMessage } = useIntl();

  const handleStartChange = useCallback((val: TypedFormFieldValue<Date> | undefined) => {
    if (val?.value) {
      const d = val.value;
      setStartDate(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`);
    } else {
      setStartDate('');
    }
  }, []);

  const handleEndChange = useCallback((val: TypedFormFieldValue<Date> | undefined) => {
    if (val?.value) {
      const d = val.value;
      setEndDate(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`);
    } else {
      setEndDate('');
    }
  }, []);

  const handleNext = () => {
    if (startDate && endDate) onNext({ StartDate: startDate, EndDate: endDate });
  };

  return (
    <Paper variant="outlined" style={{ padding: '32px 48px', minHeight: '200px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '24px', lineHeight: 1.4, textAlign: 'center' }}>
          {formatMessage({ id: 'law-portal.evalution.start.date.label' })}
        </Typography>
        <Box style={{ maxWidth: 360, width: '100%' }}>
          <DateField
            name="start-date"
            placeholder=""
            errorTexts={[]}
            initialState={currentData.StartDate ? { value: new Date(currentData.StartDate) } : undefined}
            onChange={handleStartChange}
          />
        </Box>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '24px', marginTop: '24px', lineHeight: 1.4, textAlign: 'center' }}>
          {formatMessage({ id: 'law-portal.evalution.end.date.label' })}
        </Typography>
        <Box style={{ maxWidth: 360, width: '100%' }}>
          <DateField
            name="end-date"
            placeholder=""
            errorTexts={[]}
            initialState={currentData.EndDate ? { value: new Date(currentData.EndDate) } : undefined}
            onChange={handleEndChange}
          />
        </Box>
      </div>
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleNext}
        nextLabel="form.save.and.back"
        nextDisabled={!startDate || !endDate}
        showBack={!isFirstStep}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Step 4: Reminder
// ─────────────────────────────────────────────
const Step4Reminder = ({
  onNext, onBack, onCancel, currentData, reminderOptions,
}: StepProps & { reminderOptions: { id: number; name: string }[] }) => {
  const { formatMessage } = useIntl();

  const deriveSendReminder = () => {
    if (currentData._sendReminder === true) return 'yes';
    if (currentData._sendReminder === false) return 'no';
    if (currentData.Reminder != null) return 'yes';
    return '';
  };

  const [sendReminder, setSendReminder] = useState<string>(deriveSendReminder);
  const [reminderOption, setReminderOption] = useState<number | undefined>(
    currentData.Reminder != null ? currentData.Reminder : undefined,
  );
  const resolveReminderLabel = () => {
    if (currentData._reminderLabel) return currentData._reminderLabel;
    if (currentData.Reminder != null) {
      const matched = reminderOptions.find(o => o.id === currentData.Reminder);
      return matched?.name || '';
    }
    return '';
  };
  const [reminderLabel, setReminderLabel] = useState<string>(resolveReminderLabel);

  const selectOptions = useMemo(() => reminderOptions.map(o => ({ value: o.id, label: o.name })), [reminderOptions]);
  const isValid = sendReminder === 'no' || (sendReminder === 'yes' && reminderOption != null);

  const handleReminderChange = useCallback((val: TypedFormFieldValue<number> | undefined) => {
    if (val) {
      setReminderOption(val.value);
      setReminderLabel(val.label || '');
    } else {
      setReminderOption(undefined);
      setReminderLabel('');
    }
  }, []);

  const handleNext = () => {
    if (!isValid) return;
    onNext({
      _sendReminder: sendReminder === 'yes',
      Reminder: sendReminder === 'yes' ? reminderOption : undefined,
      _reminderLabel: sendReminder === 'yes' ? reminderLabel : undefined,
    });
  };

  return (
    <Paper variant="outlined" style={{ padding: '32px 48px', minHeight: '200px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '20px', lineHeight: 1.4, textAlign: 'center' }}>
          {formatMessage({ id: 'law-portal.evalution.reminder.yes.no.qus' })}
        </Typography>
        <Box style={{ display: 'flex', gap: '12px', marginBottom: '24px', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => setSendReminder('yes')}
            style={{
              minWidth: '80px', fontWeight: 700, borderColor: PRIMARY,
              color: sendReminder === 'yes' ? '#fff' : PRIMARY,
              background: sendReminder === 'yes' ? PRIMARY : 'transparent',
            }}
          >
            {formatMessage({ id: 'law-portal.evalution.yes' })}
          </Button>
          <Button
            variant="outlined"
            onClick={() => { setSendReminder('no'); setReminderOption(undefined); setReminderLabel(''); }}
            style={{
              minWidth: '80px', fontWeight: 700, borderColor: PRIMARY,
              color: sendReminder === 'no' ? '#fff' : PRIMARY,
              background: sendReminder === 'no' ? PRIMARY : 'transparent',
            }}
          >
            {formatMessage({ id: 'law-portal.evalution.no' })}
          </Button>
        </Box>
        {sendReminder === 'yes' && (
          <>
            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '12px', lineHeight: 1.4, textAlign: 'center' }}>
              {formatMessage({ id: 'law-portal.evalution.reminder.date.label' })}
            </Typography>
            <Box style={{ maxWidth: 360, width: '100%' }}>
              <SelectField
                name="reminder-interval"
                placeholder={formatMessage({ id: 'utils.select.select' })}
                options={selectOptions}
                errorTexts={[]}
                initialState={reminderOption != null ? { value: reminderOption, label: reminderLabel } : undefined}
                onChange={handleReminderChange}
              />
            </Box>
          </>
        )}
      </div>
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleNext}
        nextLabel="form.save.and.back"
        nextDisabled={!isValid}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Step 5: Requirement type
// ─────────────────────────────────────────────
const Step5RequirementType = ({
  onNext, onBack, onCancel, currentData,
  typeOptions, onTypeSelect,
}: StepProps & { typeOptions: { id: number; name: string }[]; onTypeSelect: (t: any) => void }) => {
  const [selectedId, setSelectedId] = useState<number | null>(currentData.type ?? null);
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (typeOptions.length === 1 && selectedId == null) {
      setSelectedId(typeOptions[0].id);
      onTypeSelect(typeOptions[0]);
    }
  }, [typeOptions]);

  const handleSelect = (t: { id: number; name: string }) => {
    setSelectedId(t.id);
    onTypeSelect(t);
  };

  const handleNext = () => {
    const chosen = typeOptions.find(t => t.id === selectedId);
    if (chosen) onNext({ type: chosen.id, _typeName: chosen.name });
  };

  return (
    <Paper variant="outlined" style={{ padding: '32px 48px', minHeight: '200px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '32px', lineHeight: 1.4, textAlign: 'center' }}>
          {formatMessage({ id: 'law-portal.evalution.content.plan.label' })}
        </Typography>
        <Box style={{ maxWidth: 460, width: '100%' }}>
          <Grid container spacing={2} justifyContent="center">
            {typeOptions.map(t => (
              <Grid item key={t.id} xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleSelect(t)}
                  style={{
                    padding: '14px 20px', fontSize: '14px', fontWeight: 700, borderColor: PRIMARY,
                    color: selectedId === t.id ? '#fff' : PRIMARY,
                    background: selectedId === t.id ? PRIMARY : 'transparent',
                  }}
                >
                  {t.name}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      </div>
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleNext}
        nextLabel="form.save.and.back"
        nextDisabled={selectedId == null}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Step 6: Laws table
// ─────────────────────────────────────────────
const Step6LawsTable = ({
  selectedRows, setSelectedRows, selectedRowsData, setSelectedRowsData,
  selectedRequirementType, selectedCompanyUnit, onNext, onBack, onCancel,
}: any) => {
  const { formatMessage } = useIntl();
  return (
    <Paper variant="outlined" style={{ padding: '24px', minHeight: '200px' }}>
      {selectedRequirementType && selectedCompanyUnit && (
        <Box style={{ maxHeight: '500px', overflow: 'auto' }}>
          <ComplianceTable
            selectedRows={selectedRows}
            type={selectedRequirementType}
            setSelectedRows={setSelectedRows}
            companyUnit={{ label: selectedCompanyUnit?.name, value: selectedCompanyUnit?.id }}
            showMarkAll
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={setSelectedRowsData}
          />
        </Box>
      )}
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={onNext}
        nextLabel="form.save.and.back"
        nextDisabled={selectedRows.length === 0}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Step 7: Plan name
// ─────────────────────────────────────────────
const Step7PlanName = ({ onNext, onBack, onCancel, currentData }: StepProps) => {
  const [name, setName] = useState<string>(currentData.name || '');
  const { formatMessage } = useIntl();

  const handleNext = () => {
    if (name.trim()) onNext({ name: name.trim() });
  };

  return (
    <Paper variant="outlined" style={{ padding: '32px 48px', minHeight: '200px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '24px', lineHeight: 1.4, textAlign: 'center' }}>
          {formatMessage({ id: 'law-portal.evalution.plan.name.label' })}
        </Typography>
        <Box style={{ maxWidth: 360, width: '100%' }}>
          <AppTextField
            name="plan-name"
            placeholder=""
            errorTexts={[]}
            initialState={currentData.name ? { value: currentData.name } : undefined}
            onChange={(val) => setName(val?.value || '')}
          />
        </Box>
      </div>
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleNext}
        nextLabel="law-portal.evalution.plan.btn.save.preview"
        nextDisabled={!name.trim()}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Step 8: Preview
// ─────────────────────────────────────────────
const Step8Preview = ({
  planData, setStepData, selectedRows, setSelectedRows, selectedRowsData, setSelectedRowsData,
  requirementType, setSelectedCompanyUnit, setSelectedRequirementType,
  companyUnits, requirementTypeOptions, reminderOptions,
  onSubmit, onBack, onCancel, isLoading, planType,
}: any) => {
  const { formatMessage } = useIntl();
  const [localData, setLocalData] = useState<StepData>({ ...planData });
  const localDataRef = React.useRef<StepData>(localData);
  localDataRef.current = localData;

  const updateLocal = useCallback((updates: Partial<StepData>) => {
    setLocalData(prev => ({ ...prev, ...updates }));
  }, []);

  const prevCompanyUnitRef = React.useRef(localData.CustomerCompanyUnitId);
  const prevTypeRef = React.useRef(localData.type);

  const handleNameChange = useCallback((val: any) => updateLocal({ name: val?.value || '' }), [updateLocal]);

  const handleCompanyUnitChange = useCallback((val: any) => {
    const newId = val?.value;
    updateLocal({ CustomerCompanyUnitId: newId, _companyUnitName: val?.label || '' });
    const unit = companyUnits.find((u: any) => u.id === newId);
    if (unit) setSelectedCompanyUnit(unit);
    if (newId !== prevCompanyUnitRef.current) {
      setSelectedRows([]);
      setSelectedRowsData([]);
      prevCompanyUnitRef.current = newId;
    }
  }, [updateLocal, companyUnits, setSelectedCompanyUnit, setSelectedRows, setSelectedRowsData]);

  const handleStartDateChange = useCallback((val: any) => {
    if (val?.value) {
      const d = val.value;
      updateLocal({ StartDate: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}` });
    }
  }, [updateLocal]);

  const handleEndDateChange = useCallback((val: any) => {
    if (val?.value) {
      const d = val.value;
      updateLocal({ EndDate: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}` });
    }
  }, [updateLocal]);

  const handleContentTypeChange = useCallback((val: any) => {
    const newType = val?.value;
    updateLocal({ type: newType, _typeName: val?.label || '' });
    const reqType = requirementTypeOptions.find((t: any) => t.id === newType);
    if (reqType) setSelectedRequirementType(reqType);
    if (newType !== prevTypeRef.current) {
      setSelectedRows([]);
      setSelectedRowsData([]);
      prevTypeRef.current = newType;
    }
  }, [updateLocal, requirementTypeOptions, setSelectedRequirementType, setSelectedRows, setSelectedRowsData]);

  const handleReminderChange = useCallback((val: any) => {
    updateLocal({ Reminder: val?.value, _reminderLabel: val?.label || '', _sendReminder: val != null });
  }, [updateLocal]);

  const handleRecurringDaysChange = useCallback((val: any) => {
    updateLocal({ RecurrentInterval: val?.value ? Number(val.value) : undefined });
  }, [updateLocal]);

  const handlePreviewBack = useCallback(() => {
    const latest = localDataRef.current;
    setStepData((prev: StepData) => ({ ...prev, ...latest }));
    if (latest.CustomerCompanyUnitId != null) {
      const unit = companyUnits.find((u: any) => u.id === latest.CustomerCompanyUnitId);
      if (unit) setSelectedCompanyUnit(unit);
    }
    if (latest.type != null) {
      const reqType = requirementTypeOptions.find((t: any) => t.id === latest.type);
      if (reqType) setSelectedRequirementType(reqType);
    }
    onBack();
  }, [setStepData, onBack, companyUnits, requirementTypeOptions, setSelectedCompanyUnit, setSelectedRequirementType]);

  const handlePreviewSubmit = useCallback(() => {
    const latest = localDataRef.current;
    setStepData((prev: StepData) => ({ ...prev, ...latest }));
    onSubmit(latest);
  }, [setStepData, onSubmit]);

  const companyUnitOptions = useMemo(() => companyUnits.map((u: any) => ({ value: u.id, label: u.name })), [companyUnits]);
  const typeOptions = useMemo(() => requirementTypeOptions.map((t: any) => ({ value: t.id, label: t.name })), [requirementTypeOptions]);
  const reminderSelectOptions = useMemo(() => reminderOptions.map((o: any) => ({ value: o.id, label: o.name })), [reminderOptions]);

  const isLaws = localData.type === 0;
  const isRecurring = planType === 'recurring' || planType === 'from-completed';

  const renderDate = (date?: Date) => date ? moment(date).format('ll') : undefined;

  const renderCommaSplit = (value: string | undefined) => {
    if (value) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {value.split(',').map((val: string, index: number) => <span key={index}>{val}</span>)}
        </div>
      );
    }
    return '-';
  };

  const columns: any[] = isLaws ? [
    { title: formatMessage({ id: 'law-portal.our-laws.table.number' }), field: 'number' },
    { title: formatMessage({ id: 'law-portal.our-laws.table.law' }), field: 'name', render: (row: any) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} /> },
    { title: formatMessage({ id: 'law-portal.our-laws.table.description' }), field: 'description', render: (row: any) => <HtmlView variant="body2" value={row?.description} />, sorting: false },
    { title: formatMessage({ id: 'law-portal.our-laws.table.changeNumber' }), field: 'changeNumber' },
    { title: formatMessage({ id: 'law-portal.our-laws.table.changeEffectiveFrom' }), field: 'changeEffectiveFrom', render: (row: any) => renderDate(row.changeEffectiveFrom) },
    { title: formatMessage({ id: 'law-portal.our-laws.table.notes' }), field: 'notes', render: (row: any) => <OwnNotes {...row} />, sorting: false },
    { title: formatMessage({ id: 'law-portal.compliance.report.table.col' }), field: 'responsible', render: (row: any) => renderCommaSplit(row.responsible), sorting: false },
    { title: formatMessage({ id: 'utils.filters.processes' }), field: 'processes', render: (row: any) => renderCommaSplit(row.processes), sorting: false },
    { title: formatMessage({ id: 'utils.filters.aspects' }), field: 'aspects', render: (row: any) => renderCommaSplit(row.aspects), sorting: false },
    { title: formatMessage({ id: 'utils.filters.tags' }), field: 'tags', render: (row: any) => renderCommaSplit(row.tags), sorting: false },
    { title: formatMessage({ id: 'law-portal.our-laws.table.area' }), field: 'area' },
  ] : [
    { title: formatMessage({ id: 'law-portal.requirements.table.name' }), field: 'name', render: (row: any) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} /> },
    { title: formatMessage({ id: 'law-portal.requirements.table.description' }), field: 'description', render: (row: any) => <HtmlView variant="body2" value={row.description} />, sorting: false },
    { title: formatMessage({ id: 'law-portal.requirements.table.area' }), field: 'area' },
    { title: formatMessage({ id: 'law-portal.requirements.table.changedDate' }), field: 'changedDate', render: (row: any) => renderDate(row.changedDate) },
    { title: formatMessage({ id: 'law-portal.requirements.table.notes' }), field: 'notes', render: (row: any) => <OwnNotes {...row} />, sorting: false },
    { title: formatMessage({ id: 'law-portal.compliance.report.table.col' }), field: 'responsible', render: (row: any) => renderCommaSplit(row.responsible), sorting: false },
    { title: formatMessage({ id: 'utils.filters.processes' }), field: 'processes', render: (row: any) => renderCommaSplit(row.processes), sorting: false },
    { title: formatMessage({ id: 'utils.filters.aspects' }), field: 'aspects', render: (row: any) => renderCommaSplit(row.aspects), sorting: false },
    { title: formatMessage({ id: 'utils.filters.tags' }), field: 'tags', render: (row: any) => renderCommaSplit(row.tags), sorting: false },
  ];

  return (
    <Paper variant="outlined" style={{ padding: '24px', minHeight: '200px' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <AppTextField
            name="preview-name"
            label={formatMessage({ id: 'law-portal.legal.planned.form.name' })}
            required
            placeholder="Enter plan name"
            errorTexts={[]}
            initialState={localData.name ? { value: localData.name } : undefined}
            onChange={handleNameChange}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SelectField
            name="preview-company-unit"
            label={formatMessage({ id: 'law-portal.legal.planned.form.company.unit' })}
            required
            disable
            placeholder="Select..."
            options={companyUnitOptions}
            errorTexts={[]}
            initialState={localData.CustomerCompanyUnitId != null ? { value: localData.CustomerCompanyUnitId, label: localData._companyUnitName } : undefined}
            onChange={handleCompanyUnitChange}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <DateField
            name="preview-start-date"
            label={formatMessage({ id: 'law-portal.legal.planned.form.start.date' })}
            required
            placeholder="YYYY-MM-DD"
            errorTexts={[]}
            initialState={localData.StartDate ? { value: new Date(localData.StartDate) } : undefined}
            onChange={handleStartDateChange}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <DateField
            name="preview-end-date"
            label={formatMessage({ id: 'law-portal.legal.planned.form.end.date' })}
            required
            placeholder="YYYY-MM-DD"
            errorTexts={[]}
            initialState={localData.EndDate ? { value: new Date(localData.EndDate) } : undefined}
            onChange={handleEndDateChange}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: '8px', alignItems: 'center' }}>
        <Grid item xs={12} sm={4}>
          <SelectField
            name="preview-content-type"
            label={formatMessage({ id: 'law-portal.create-plan.form-field-type' })}
            required
            disable
            placeholder="Select..."
            options={typeOptions}
            errorTexts={[]}
            initialState={localData.type != null ? { value: localData.type, label: requirementType?.name || localData._typeName } : undefined}
            onChange={handleContentTypeChange}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SelectField
            name="preview-reminder"
            label={formatMessage({ id: 'law-portal.compliance.create.plan.field.utskick' })}
            placeholder="Select..."
            options={reminderSelectOptions}
            errorTexts={[]}
            initialState={localData.Reminder != null ? { value: localData.Reminder, label: localData._reminderLabel } : undefined}
            onChange={handleReminderChange}
            icon={
              <Tooltip title={formatMessage({ id: 'law-portal.compliance.create.plan.field.utskick.tooltip.msg' })} placement="right-start">
                <InfoOutlined style={{ fontSize: '20px' }} />
              </Tooltip>
            }
          />
        </Grid>
        {isRecurring && (
          <Grid item xs={12} sm={4}>
            <AppTextField
              name="preview-recurring-days"
              label={formatMessage({ id: 'law-portal.compliance.create.plan.field.intervall' })}
              placeholder={formatMessage({ id: 'law-portal.legal.number.of.day' })}
              type="number"
              errorTexts={[]}
              initialState={localData.RecurrentInterval != null ? { value: localData.RecurrentInterval.toString() } : undefined}
              onChange={handleRecurringDaysChange}
              icon={
                <Tooltip title={formatMessage({ id: 'law-portal.compliance.create.plan.field.intervall.tooltip.msg' })} placement="right-start">
                  <InfoOutlined style={{ fontSize: '20px' }} />
                </Tooltip>
              }
            />
          </Grid>
        )}
      </Grid>

      <Box style={{ marginTop: '16px', maxHeight: '400px', overflowX: 'auto', overflowY: 'auto', width: '100%' }}>
        <MTable
          columns={columns}
          data={(selectedRowsData || []).map(({ tableData, ...rest }: any) => ({ ...rest }))}
          options={{ search: false, paging: false, toolbar: false, sorting: false, draggable: false, columnsButton: false }}
          components={{}}
        />
      </Box>
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <StepButtonBar
        onCancel={onCancel}
        onBack={handlePreviewBack}
        onNext={handlePreviewSubmit}
        nextLabel="Save and create"
        isLoading={isLoading}
        formatMessage={formatMessage}
      />
    </Paper>
  );
};

export default CreatePlanWizard;